import axios from "axios";
import React, { useState } from "react";
import {
  Select,
  Button,
  HStack,
  Input,
  FormControl,
  FormLabel,
  VStack,
  Box,
  Text,
  FormErrorMessage,
} from "@chakra-ui/react";
import useNotification from "../../utils/useNotification";
import { OrderStatus, TransactionType } from "../Orders/enums";

interface OrderPayload {
  orderStatus: string;
  allocatedTo?: Array<{
    invoiceInfo: {
      invoiceNo: string;
    };
  }>;
  transactionType?: string;
}

const InsertTestOrder: React.FC = () => {
  const [orderStatus, setOrderStatus] = useState<string>("");
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState<string>("");
  const [transactionType, setTransactionType] = useState<string>("");
  const [invoiceError, setInvoiceError] = useState<string>("");
  const MAX_INVOICE_LENGTH = 16;

  const toast = useNotification();
  const handleOrderStatusChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setOrderStatus(event.target.value);
    setInvoiceNumber("");
    setTransactionType("");
    setInvoiceError("");
  };
  const handleInvoiceNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    if (value.length <= MAX_INVOICE_LENGTH) {
      setInvoiceNumber(value);
      setInvoiceError("");
    } else {
      setInvoiceError("Invoice number cannot exceed 16 characters");
    }
  };
  const handleExecuteClick = async () => {
    setIsPlacingOrder(true);
    const url = process.env.REACT_APP_URL_FOR_CREATE_TEST_ORDER!;
    const payload: OrderPayload = { orderStatus };

    if (orderStatus === OrderStatus.READY && invoiceNumber.trim()) {
      payload.allocatedTo = [
        {
          invoiceInfo: {
            invoiceNo: invoiceNumber.trim(),
          },
        },
      ];
    }
    if (transactionType.trim()) {
      payload.transactionType = transactionType.trim();
    }
    try {
      const response = await axios.post(url, payload, {
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
        },
      });
      toast({
        description: `Order placed successfully! Order No: ${response.data.orderNo}`,
        status: "success",
      });
    } catch (error) {
      console.error("Error placing order:", error);
      toast({
        description: "Error placing order!",
        status: "error",
      });
    } finally {
      setIsPlacingOrder(false);
    }
  };
  return (
    <VStack spacing={6} align="stretch" width="100%">
      <Text fontSize="xl" fontWeight="bold" color="gray.700">
        Create Test Order
      </Text>

      <FormControl isRequired>
        <FormLabel fontSize="md" fontWeight="medium" color="gray.700">
          Order Status
        </FormLabel>
        <Select
          value={orderStatus}
          onChange={handleOrderStatusChange}
          bgColor="white"
          placeholder="Select Order Status"
          size="md"
          borderColor="gray.300"
          _hover={{ borderColor: "gray.400" }}
          _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
          isDisabled={isPlacingOrder}
        >
          <option value={OrderStatus.READY}>SUPPLIER READY</option>
          <option value={OrderStatus.ACK}>ACK</option>
          <option value={OrderStatus.READY_TO_DISPATCH}>
            READY TO DISPATCH
          </option>
          <option value={OrderStatus.DELIVERED}>DELIVERED</option>
        </Select>
      </FormControl>

      <HStack spacing={6} align="start" width="100%">
        {orderStatus === OrderStatus.READY && (
          <FormControl isInvalid={!!invoiceError} flex={1}>
            <FormLabel fontSize="md" fontWeight="medium" color="gray.700">
              Invoice Number
            </FormLabel>
            <Input
              value={invoiceNumber}
              onChange={handleInvoiceNumberChange}
              placeholder="Enter invoice number"
              bgColor="white"
              size="md"
              borderColor="gray.300"
              _hover={{ borderColor: "gray.400" }}
              _focus={{
                borderColor: "blue.500",
                boxShadow: "0 0 0 1px #3182ce",
              }}
              isDisabled={isPlacingOrder}
            />
            <FormErrorMessage>{invoiceError}</FormErrorMessage>
          </FormControl>
        )}

        {orderStatus && (
          <FormControl flex={1}>
            <FormLabel fontSize="md" fontWeight="medium" color="gray.700">
              Transaction Type
            </FormLabel>
            <Select
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value)}
              bgColor="white"
              placeholder="Select Transaction Type"
              size="md"
              borderColor="gray.300"
              _hover={{ borderColor: "gray.400" }}
              _focus={{
                borderColor: "blue.500",
                boxShadow: "0 0 0 1px #3182ce",
              }}
              isDisabled={isPlacingOrder}
            >
              <option value={TransactionType.FACILITATION}>Facilitation</option>
              <option value={TransactionType.TRADING}>Trading</option>
            </Select>
          </FormControl>
        )}
      </HStack>

      <Button
        width="fit-content"
        onClick={handleExecuteClick}
        ml="auto"
        colorScheme="blue"
        size="md"
        px={6}
        isLoading={isPlacingOrder}
        loadingText="Placing Order"
        isDisabled={!orderStatus}
        _hover={{ boxShadow: "lg" }}
      >
        Create Test Order
      </Button>
    </VStack>
  );
};
export default InsertTestOrder;
